// 图书解析引擎实现

import type {
  BookSource,
  BookInfo,
  Chapter,
  ChapterContent,
  SearchQuery,
  SearchResult
} from '@/types/reading';

export class BookParser {
  private readonly USER_AGENT = 'Mozilla/5.0 (iPhone; CPU iPhone OS 14_0 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/14.0 Mobile/15E148 Safari/604.1';
  private readonly REQUEST_TIMEOUT = 15000; // 15秒超时

  // ============================================================================
  // 搜索图书
  // ============================================================================

  async searchBooks(query: SearchQuery, source: BookSource): Promise<SearchResult> {
    try {
      // 1. 构建搜索URL
      const searchUrl = this.buildSearchUrl(query.keyword, source);
      
      // 2. 获取搜索页面HTML
      const html = await this.fetchHtml(searchUrl);
      
      // 3. 解析搜索结果
      const books = await this.parseSearchResults(html, source);
      
      // 4. 应用筛选和排序
      const filteredBooks = this.filterAndSortBooks(books, query);
      
      // 5. 分页处理
      const startIndex = (query.page - 1) * query.pageSize;
      const endIndex = startIndex + query.pageSize;
      const paginatedBooks = filteredBooks.slice(startIndex, endIndex);

      return {
        books: paginatedBooks,
        total: filteredBooks.length,
        page: query.page,
        pageSize: query.pageSize,
        suggestions: this.generateSearchSuggestions(query.keyword),
        searchTime: Date.now()
      };

    } catch (error) {
      console.error('Search books failed:', error);
      return {
        books: [],
        total: 0,
        page: query.page,
        pageSize: query.pageSize,
        suggestions: [],
        searchTime: Date.now()
      };
    }
  }

  private buildSearchUrl(keyword: string, source: BookSource): string {
    let searchUrl = source.ruleSearch.ruleSearchUrl;
    
    // 替换搜索关键词
    searchUrl = searchUrl.replace(/searchKey/g, encodeURIComponent(keyword));
    searchUrl = searchUrl.replace(/searchPage/g, '1'); // 默认第一页
    
    return searchUrl;
  }

  private async parseSearchResults(html: string, source: BookSource): Promise<BookInfo[]> {
    const books: BookInfo[] = [];
    
    try {
      // 解析搜索列表
      const bookElements = this.parseRule(html, source.ruleSearch.ruleSearchList);
      
      for (let i = 0; i < bookElements.length; i++) {
        const element = bookElements[i];
        
        try {
          const book: BookInfo = {
            id: this.generateBookId(source.id, i),
            title: this.parseRule(element, source.ruleSearch.ruleSearchName)[0] || '',
            author: this.parseRule(element, source.ruleSearch.ruleSearchAuthor)[0] || '',
            coverUrl: this.parseRule(element, source.ruleSearch.ruleSearchCoverUrl || '')[0],
            description: '', // 搜索结果通常不包含详细描述
            category: this.parseRule(element, source.ruleSearch.ruleSearchKind || '')[0] || 'Unknown',
            tags: [],
            chapters: [],
            sourceId: source.id,
            sourceUrl: this.parseRule(element, source.ruleSearch.ruleSearchNoteUrl)[0] || '',
            lastChapter: this.parseRule(element, source.ruleSearch.ruleSearchLastChapter || '')[0],
            updateTime: new Date()
          };

          // 验证必需字段
          if (book.title && book.sourceUrl) {
            // 处理相对URL
            book.sourceUrl = this.resolveUrl(book.sourceUrl, source.bookSourceUrl);
            if (book.coverUrl) {
              book.coverUrl = this.resolveUrl(book.coverUrl, source.bookSourceUrl);
            }
            
            books.push(book);
          }

        } catch (error) {
          console.warn(`Failed to parse book ${i}:`, error);
        }
      }

    } catch (error) {
      console.error('Failed to parse search results:', error);
    }

    return books;
  }

  // ============================================================================
  // 获取图书详情
  // ============================================================================

  async getBookDetail(bookUrl: string, source: BookSource): Promise<BookInfo | null> {
    try {
      // 1. 获取图书详情页面HTML
      const html = await this.fetchHtml(bookUrl);
      
      // 2. 解析图书基本信息
      const bookInfo = await this.parseBookInfo(html, source, bookUrl);
      
      // 3. 解析章节列表
      const chapters = await this.parseChapterList(html, source);
      
      if (bookInfo) {
        bookInfo.chapters = chapters;
        return bookInfo;
      }

      return null;

    } catch (error) {
      console.error('Get book detail failed:', error);
      return null;
    }
  }

  private async parseBookInfo(html: string, source: BookSource, bookUrl: string): Promise<BookInfo | null> {
    try {
      const title = this.parseRule(html, source.ruleBookInfo.ruleBookName)[0];
      const author = this.parseRule(html, source.ruleBookInfo.ruleBookAuthor)[0];
      
      if (!title || !author) {
        return null;
      }

      const coverUrl = this.parseRule(html, source.ruleBookInfo.ruleCoverUrl || '')[0];
      const description = this.parseRule(html, source.ruleBookInfo.ruleIntroduce || '')[0];

      return {
        id: this.generateBookId(source.id, bookUrl),
        title: this.cleanText(title),
        author: this.cleanText(author),
        coverUrl: coverUrl ? this.resolveUrl(coverUrl, source.bookSourceUrl) : undefined,
        description: description ? this.cleanText(description) : undefined,
        category: 'Unknown', // 需要从其他地方获取
        tags: [],
        chapters: [], // 将在后续填充
        sourceId: source.id,
        sourceUrl: bookUrl,
        updateTime: new Date()
      };

    } catch (error) {
      console.error('Failed to parse book info:', error);
      return null;
    }
  }

  private async parseChapterList(html: string, source: BookSource): Promise<Chapter[]> {
    const chapters: Chapter[] = [];
    
    try {
      const chapterElements = this.parseRule(html, source.ruleBookInfo.ruleChapterList);
      
      for (let i = 0; i < chapterElements.length; i++) {
        const element = chapterElements[i];
        
        try {
          const title = this.parseRule(element, source.ruleBookInfo.ruleChapterName)[0];
          const url = this.parseRule(element, source.ruleBookInfo.ruleContentUrl)[0];
          
          if (title && url) {
            const chapter: Chapter = {
              id: this.generateChapterId(i),
              title: this.cleanText(title),
              url: this.resolveUrl(url, source.bookSourceUrl),
              index: i
            };
            
            chapters.push(chapter);
          }

        } catch (error) {
          console.warn(`Failed to parse chapter ${i}:`, error);
        }
      }

    } catch (error) {
      console.error('Failed to parse chapter list:', error);
    }

    return chapters;
  }

  // ============================================================================
  // 获取章节内容
  // ============================================================================

  async getChapterContent(chapterUrl: string, source: BookSource): Promise<ChapterContent | null> {
    try {
      // 1. 获取章节页面HTML
      const html = await this.fetchHtml(chapterUrl);
      
      // 2. 解析章节内容
      const content = this.parseRule(html, source.ruleContent.ruleBookContent)[0];
      
      if (!content) {
        return null;
      }

      // 3. 解析章节标题（如果页面中有）
      const title = this.parseRule(html, source.ruleBookInfo.ruleBookName)[0] || 
                   this.extractChapterTitleFromUrl(chapterUrl);

      // 4. 解析下一章链接
      const nextChapterUrl = source.ruleContent.ruleContentUrlNext ? 
        this.parseRule(html, source.ruleContent.ruleContentUrlNext)[0] : undefined;

      return {
        chapterId: this.generateChapterIdFromUrl(chapterUrl),
        title: this.cleanText(title),
        content: this.cleanContent(content),
        nextChapterUrl: nextChapterUrl ? this.resolveUrl(nextChapterUrl, source.bookSourceUrl) : undefined
      };

    } catch (error) {
      console.error('Get chapter content failed:', error);
      return null;
    }
  }

  // ============================================================================
  // 规则解析器
  // ============================================================================

  private parseRule(html: string, rule: string): string[] {
    if (!rule || !html) {
      return [];
    }

    try {
      // 处理复合规则（用|分隔的多个规则）
      const rules = rule.split('|');
      
      for (const singleRule of rules) {
        const result = this.parseSingleRule(html, singleRule.trim());
        if (result.length > 0) {
          return result;
        }
      }

      return [];

    } catch (error) {
      console.warn('Rule parse error:', error);
      return [];
    }
  }

  private parseSingleRule(html: string, rule: string): string[] {
    // 处理不同类型的规则
    
    // CSS选择器规则
    if (rule.includes('@')) {
      return this.parseCssRule(html, rule);
    }
    
    // XPath规则（简化实现）
    if (rule.startsWith('//') || rule.startsWith('/')) {
      return this.parseXPathRule(html, rule);
    }
    
    // 正则表达式规则
    if (rule.startsWith('##') && rule.endsWith('##')) {
      return this.parseRegexRule(html, rule);
    }
    
    // 默认作为CSS选择器处理
    return this.parseCssRule(html, rule);
  }

  private parseCssRule(html: string, rule: string): string[] {
    try {
      // 解析CSS选择器和属性提取规则
      const parts = rule.split('@');
      const selector = parts[0];
      const attribute = parts[1] || 'text';
      
      // 创建临时DOM来解析
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const elements = doc.querySelectorAll(selector);
      
      const results: string[] = [];
      
      elements.forEach(element => {
        let value = '';
        
        switch (attribute) {
          case 'text':
            value = element.textContent || '';
            break;
          case 'html':
            value = element.innerHTML || '';
            break;
          case 'href':
            value = (element as HTMLAnchorElement).href || '';
            break;
          case 'src':
            value = (element as HTMLImageElement).src || '';
            break;
          default:
            value = element.getAttribute(attribute) || '';
            break;
        }
        
        if (value) {
          results.push(value.trim());
        }
      });
      
      return results;

    } catch (error) {
      console.warn('CSS rule parse error:', error);
      return [];
    }
  }

  private parseXPathRule(html: string, rule: string): string[] {
    // XPath解析的简化实现
    // 在浏览器环境中，XPath支持有限，这里提供基本实现
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // 简单的XPath到CSS选择器转换
      let cssSelector = rule;
      cssSelector = cssSelector.replace(/\/\/(\w+)/g, '$1');
      cssSelector = cssSelector.replace(/\[@(\w+)='([^']+)'\]/g, '[$1="$2"]');
      
      const elements = doc.querySelectorAll(cssSelector);
      const results: string[] = [];
      
      elements.forEach(element => {
        const text = element.textContent?.trim();
        if (text) {
          results.push(text);
        }
      });
      
      return results;

    } catch (error) {
      console.warn('XPath rule parse error:', error);
      return [];
    }
  }

  private parseRegexRule(html: string, rule: string): string[] {
    try {
      // 提取正则表达式
      const regexStr = rule.slice(2, -2); // 移除 ## 标记
      const regex = new RegExp(regexStr, 'g');
      
      const results: string[] = [];
      let match;
      
      while ((match = regex.exec(html)) !== null) {
        // 如果有捕获组，使用第一个捕获组，否则使用整个匹配
        const value = match[1] || match[0];
        if (value) {
          results.push(value.trim());
        }
      }
      
      return results;

    } catch (error) {
      console.warn('Regex rule parse error:', error);
      return [];
    }
  }

  // ============================================================================
  // 网络请求
  // ============================================================================

  private async fetchHtml(url: string): Promise<string> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.REQUEST_TIMEOUT);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'User-Agent': this.USER_AGENT,
          'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
          'Accept-Language': 'zh-CN,zh;q=0.9,en;q=0.8',
          'Accept-Encoding': 'gzip, deflate',
          'Cache-Control': 'no-cache'
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.text();

    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error('Request timeout');
        }
        throw error;
      }
      
      throw new Error('Unknown fetch error');
    }
  }

  // ============================================================================
  // 工具方法
  // ============================================================================

  private filterAndSortBooks(books: BookInfo[], query: SearchQuery): BookInfo[] {
    let filtered = books;

    // 按作者筛选
    if (query.author) {
      filtered = filtered.filter(book => 
        book.author.toLowerCase().includes(query.author!.toLowerCase())
      );
    }

    // 按分类筛选
    if (query.category) {
      filtered = filtered.filter(book => 
        book.category.toLowerCase() === query.category!.toLowerCase()
      );
    }

    // 按标签筛选
    if (query.tags && query.tags.length > 0) {
      filtered = filtered.filter(book => 
        query.tags!.some(tag => 
          book.tags.some(bookTag => 
            bookTag.toLowerCase().includes(tag.toLowerCase())
          )
        )
      );
    }

    // 排序
    switch (query.sortBy) {
      case 'rating':
        filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      case 'updateTime':
        filtered.sort((a, b) => b.updateTime.getTime() - a.updateTime.getTime());
        break;
      case 'wordCount':
        filtered.sort((a, b) => (b.wordCount || 0) - (a.wordCount || 0));
        break;
      case 'popularity':
        // 简单的流行度计算（基于评分和字数）
        filtered.sort((a, b) => {
          const scoreA = (a.rating || 0) * Math.log((a.wordCount || 1000) / 1000);
          const scoreB = (b.rating || 0) * Math.log((b.wordCount || 1000) / 1000);
          return scoreB - scoreA;
        });
        break;
      default: // relevance
        // 按标题匹配度排序
        const keyword = query.keyword.toLowerCase();
        filtered.sort((a, b) => {
          const scoreA = this.calculateRelevanceScore(a, keyword);
          const scoreB = this.calculateRelevanceScore(b, keyword);
          return scoreB - scoreA;
        });
        break;
    }

    return filtered;
  }

  private calculateRelevanceScore(book: BookInfo, keyword: string): number {
    let score = 0;
    
    // 标题匹配
    if (book.title.toLowerCase().includes(keyword)) {
      score += 10;
      if (book.title.toLowerCase().startsWith(keyword)) {
        score += 5; // 开头匹配加分
      }
    }
    
    // 作者匹配
    if (book.author.toLowerCase().includes(keyword)) {
      score += 5;
    }
    
    // 描述匹配
    if (book.description && book.description.toLowerCase().includes(keyword)) {
      score += 2;
    }
    
    return score;
  }

  private generateSearchSuggestions(keyword: string): string[] {
    // 简单的搜索建议生成
    const suggestions: string[] = [];
    
    if (keyword.length > 2) {
      // 添加一些常见的搜索建议
      suggestions.push(`${keyword} 小说`);
      suggestions.push(`${keyword} 全集`);
      suggestions.push(`${keyword} 最新章节`);
    }
    
    return suggestions;
  }

  private resolveUrl(url: string, baseUrl: string): string {
    try {
      return new URL(url, baseUrl).href;
    } catch {
      return url;
    }
  }

  private cleanText(text: string): string {
    return text
      .replace(/\s+/g, ' ') // 合并多个空白字符
      .replace(/[\r\n\t]/g, ' ') // 替换换行符和制表符
      .trim();
  }

  private cleanContent(content: string): string {
    return content
      .replace(/<[^>]*>/g, '') // 移除HTML标签
      .replace(/&nbsp;/g, ' ') // 替换HTML实体
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\s+/g, ' ') // 合并多个空白字符
      .trim();
  }

  private generateBookId(sourceId: string, identifier: string | number): string {
    const hash = this.simpleHash(sourceId + identifier.toString());
    return `book_${sourceId}_${hash}`;
  }

  private generateChapterId(index: number): string {
    return `chapter_${index}`;
  }

  private generateChapterIdFromUrl(url: string): string {
    const hash = this.simpleHash(url);
    return `chapter_${hash}`;
  }

  private extractChapterTitleFromUrl(url: string): string {
    // 从URL中提取章节标题的简单实现
    const matches = url.match(/\/([^\/]+)\.html?$/);
    return matches ? decodeURIComponent(matches[1]) : 'Unknown Chapter';
  }

  private simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36);
  }
}